import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { Menu } from "@mui/material";
import { getBackendUrl } from "@Utils";
import React, { useCallback, useEffect, useState } from "react";
import { untranslated, useTranslation } from "@i18n";
import { useDispatch, useSelector } from "react-redux";
import axiosWrapper from "../../../../../services/axiosWrapper";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import Text from "@CubeComponents/html/Text/Text";
import { convertDate } from "@Services/luxonHelper";
import { useParams } from "react-router-dom";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import VersionNotesComponent from "@Components/VersionNotes";
import { RootState } from "src/reducers";
import { Note, ScheduleVersion } from "@WebTypes/scheduleDomain";
import type {
  DeleteScheduleVersionNoteResponse,
  GetPublishedVersionsResponse,
} from "@ServerTypes/scheduleVersions/scheduleVersions.schema";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { get } from "lodash";
import { folderPermissions } from "@Helpers";

type ScheduleVersionsContentProps = Readonly<{
  scheduleId: string;
}>;

function ScheduleVersionsContent({ scheduleId }: ScheduleVersionsContentProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const [noteText, setNoteText] = useState("");
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [publishedVersions, setPublishedVersions] = useState<ScheduleVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<ScheduleVersion | null>(null);
  const [addNoteModalVisible, setAddNoteModalVisible] = useState(false);
  const [versionHistoryAnchorEl, setVersionHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
    totalCount: 0,
  });
  const [loading, setLoading] = useState(false);

  const versionHistoryOpen = Boolean(versionHistoryAnchorEl);
  const { userDetails, GanttDetails } = useSelector((state: RootState) => state);
  const { RESOURCE_MODIFY } = folderPermissions();
  const canModify = (GanttDetails as any)?.userPermissions?.includes(RESOURCE_MODIFY.id);

  const getProfile = (type: string) => {
    return <UserNameTagComponent id={userDetails._id} size={type} chipBackground="#6BBBE340" />;
  };

  const handleVersionHistoryModalClose = () => {
    setVersionHistoryAnchorEl(null);
    setSelectedVersion(null);
  };

  const handleVersionHistoryModalOpen = (event: React.MouseEvent<HTMLElement>, version: ScheduleVersion) => {
    setSelectedVersion(version);
    setVersionHistoryAnchorEl(event.currentTarget);
  };

  const handleAddNoteClick = (version: ScheduleVersion, note: Note | null = null) => {
    setSelectedVersion(version);
    if (note) {
      setEditingNote(note);
      setNoteText(note.note || "");
    }
    setAddNoteModalVisible(true);
    setVersionHistoryAnchorEl(null);
  };

  const handleAddNoteCancel = () => {
    setAddNoteModalVisible(false);
    setNoteText("");
    setSelectedVersion(null);
    setEditingNote(null);
  };

  const handleSchedulePreview = () => {
    const url = `/project/${projectId}/schedule/${scheduleId}/version/${selectedVersion?._id}?selectedTab=Gantt`;
    window.open(url, "_blank");
  };

  const fetchPublishedVersions = useCallback(
    async (page = 1) => {
      setLoading(true);
      const payload = {
        scheduleId: scheduleId,
        page: page,
        limit: 5,
      };

      const res = await axiosWrapper.post<GetPublishedVersionsResponse>(
        `${getBackendUrl()}/scheduleVersions/getPublishedVersions`,
        payload,
      );
      const data = res.data.data;

      if (data) {
        const versions = (data.versions || data.versionHistory) as ScheduleVersion[];
        if (page === 1) {
          setPublishedVersions(versions);
        } else {
          setPublishedVersions((prev: ScheduleVersion[]) => [...prev, ...versions]);
        }
        setPagination(
          data.pagination as { currentPage: number; hasNextPage: boolean; hasPrevPage: boolean; totalCount: number },
        );
      }
      setLoading(false);
    },
    [scheduleId],
  );

  const handleLoadMore = () => {
    if (pagination.hasNextPage && !loading) {
      fetchPublishedVersions(pagination.currentPage + 1);
    }
  };

  useEffect(() => {
    fetchPublishedVersions(1);
  }, [fetchPublishedVersions]);

  const handleDeleteNote = async (versionId: string, noteId: string) => {
    const payload = {
      versionId: versionId,
      noteId: noteId,
    };
    const res = await axiosWrapper.post<DeleteScheduleVersionNoteResponse>(
      `${getBackendUrl()}/scheduleVersions/deleteVersionNote`,
      payload,
    );

    if (res.status) {
      dispatch(
        showToastNotification({
          content: t("note_deleted_successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      fetchPublishedVersions();
      setVersionHistoryAnchorEl(null);
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.message ?? ""),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  return (
    <div className="flex flex-col p-3">
      <div className="flex flex-col items-start">
        <div className="flex flex-col w-full gap-3 max-h-[80vh] overflow-auto">
          {publishedVersions.length === 0 ? (
            <Text className="text-text_light text-center">{t("No Version History")}!</Text>
          ) : (
            <>
              {publishedVersions.map((version: ScheduleVersion) => (
                <div key={version._id} className="w-full flex mb-9">
                  <p className="font-semibold text-primary_text me-5">
                    {version.versionNumber ? `V${version.versionNumber}` : t("Draft")}
                  </p>
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex flex-col justify-center flex-1 min-w-0 group">
                      <div className="flex justify-between items-center group">
                        <p className="">{convertDate(version.updatedDate, "dateForText")}</p>
                        <div className="hidden group-hover:block transition-opacity duration-200">
                          <IconComponent
                            name="threedots"
                            className="cursor-pointer"
                            onClick={(e) => handleVersionHistoryModalOpen(e, version)}
                            width="1rem"
                            height="1rem"
                          />
                        </div>
                      </div>
                      <div className="flex flex-row items-center mt-2">
                        <Text className="me-2">{t("Published by")}</Text>
                        <div className="w-fit flex flex-row items-center gap-2 border border-gradient_color rounded-full bg-[#6BBBE340">
                          {getProfile("XS")}
                        </div>
                      </div>
                    </div>

                    {get(version, "notes.length", 0) > 0 &&
                      get(version, "notes", []).map((note: Note) => (
                        <div key={note._id}>
                          <div className="flex flex-row w-full group justify-between mt-2">
                            <div className="flex flex-row items-start justify-start w-full overflow-hidden">
                              <ToolTipComponent
                                position="bottom"
                                tooltipStyles={{ maxWidth: "none !important" }}
                                content={t("add_note", {
                                  fullName: note.createdBy.fullName,
                                  createdDate: convertDate(note.createdDate, "dateForText"),
                                })}
                              >
                                <div className="shrink-0">
                                  <IconComponent name="Note" className="h-5 w-5 me-2" />
                                </div>
                              </ToolTipComponent>
                              <div className="min-w-0 flex-1 overflow-hidden break-words whitespace-pre-wrap">
                                <SimpleEditor value={note.note} editable={false} showToolbar={false} />
                              </div>
                              {canModify && (
                                <div className="hidden group-hover:flex flex-row gap-x-4 ms-4">
                                  <IconComponent
                                    name="editProfile"
                                    onClick={() => handleAddNoteClick(version, note)}
                                    className="cursor-pointer h-5 w-5"
                                  />
                                  <IconComponent
                                    name="delete"
                                    onClick={() => handleDeleteNote(version._id, note._id)}
                                    className="cursor-pointer h-5 w-5"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              {/* Menu for version actions */}
              <Menu
                slotProps={{
                  list: {
                    "aria-labelledby": "fade-button",
                    sx: {
                      py: 0,
                    },
                  },
                  paper: {
                    sx: {
                      border: "1px solid #DCE8F5",
                      borderRadius: 0,
                      width: "9.375rem",
                      ml: 1,
                      boxShadow: "none",
                    },
                  },
                }}
                anchorEl={versionHistoryAnchorEl}
                open={versionHistoryOpen}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                onClose={handleVersionHistoryModalClose}
              >
                <div className="flex flex-col items-start w-full">
                  <MenuItemComponent
                    onClick={handleSchedulePreview}
                    className="w-full text-start px-2 py-3 hover:bg-selection"
                  >
                    <Text>{t("Preview")}</Text>
                  </MenuItemComponent>
                  {selectedVersion?.notes && selectedVersion?.notes.length > 0 ? (
                    <div className="flex flex-col w-full">
                      <MenuItemComponent
                        onClick={() => {
                          if (selectedVersion) {
                            handleAddNoteClick(selectedVersion, selectedVersion.notes?.[0] || null);
                          }
                        }}
                        className="w-full text-start px-2 py-3 hover:bg-selection"
                        disabled={!canModify}
                      >
                        <Text>{t("Edit Note")}</Text>
                      </MenuItemComponent>
                      <MenuItemComponent
                        onClick={() => {
                          if (selectedVersion.notes?.[0]) {
                            handleDeleteNote(selectedVersion._id, selectedVersion.notes[0]._id);
                          }
                        }}
                        className="w-full text-start px-2 py-3 hover:bg-selection"
                        disabled={!canModify}
                      >
                        <Text>{t("Delete")}</Text>
                      </MenuItemComponent>
                    </div>
                  ) : (
                    <MenuItemComponent
                      onClick={() => {
                        if (selectedVersion) {
                          handleAddNoteClick(selectedVersion);
                        }
                      }}
                      className="w-full text-start px-2 py-3 hover:bg-selection"
                      disabled={!canModify}
                    >
                      <Text>{t("Add Note")}</Text>
                    </MenuItemComponent>
                  )}
                </div>
              </Menu>

              {pagination.hasNextPage && (
                <div className="flex justify-start text-start font-semibold text-sm">
                  {loading ? (
                    <Text>{t("Loading")}...</Text>
                  ) : (
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      className="cursor-pointer border-none bg-none"
                      onClick={handleLoadMore}
                    >
                      <Text>{t("Load More")}</Text>
                    </ButtonComponent>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <VersionNotesComponent
        isVisible={addNoteModalVisible}
        onClose={handleAddNoteCancel}
        module="schedule"
        moduleData={{
          versionId: selectedVersion?._id,
          noteId: editingNote?._id,
        }}
        existingNote={noteText}
        onSuccess={() => {
          fetchPublishedVersions();
        }}
      />
    </div>
  );
}

export default ScheduleVersionsContent;
